import { type Engine, type GameDefinition, type Unsubscribe } from '@kite/engine';
import { FullscreenToggle } from './FullscreenToggle';
import { GameChrome } from './GameChrome';
import { HomeScreen } from './HomeScreen';
import { findGame, GAME_REGISTRY } from './registry';
import { Router } from './Router';

/** Construction options for {@link PortalShell}. */
export interface PortalShellOptions {
  /** The running engine. The shell activates scenes on it and never touches its systems. */
  readonly engine: Engine;
  /** Element the HTML chrome is appended to, layered over the canvas. */
  readonly chrome: HTMLElement;
}

/**
 * The portal itself: a picker, a game and the way between them.
 *
 * Navigation is the shell's only responsibility. It reads the address, builds the chosen game's scene
 * and activates it, and knows nothing about what any game contains. A game is reached through its
 * {@link GameDefinition}, which is the whole contract between the portal and the packages it hosts.
 *
 * One engine outlives every game. Switching games swaps the scene rather than tearing down the
 * renderer, so returning to the picker and starting something else costs a scene build and no GPU
 * work, and the asset cache carries over.
 */
export class PortalShell {
  readonly #engine: Engine;
  readonly #home: HomeScreen;
  readonly #gameChrome: GameChrome;
  readonly #fullscreenToggle: FullscreenToggle | undefined;
  readonly #router: Router;
  readonly #unwatchFullscreen: Unsubscribe | undefined;

  /*
   * Set the moment fullscreen is left, whoever left it, and cleared the moment it is entered again.
   * A grown-up who steps out of fullscreen has said something, and starting the next game must not
   * argue with it.
   */
  #fullscreenDeclined = false;

  constructor(options: PortalShellOptions) {
    this.#engine = options.engine;

    this.#router = new Router((gameId) => {
      this.#navigate(gameId);
    });
    this.#home = new HomeScreen(GAME_REGISTRY, (gameId) => {
      this.#choose(gameId);
    });
    this.#gameChrome = new GameChrome(() => {
      this.#router.goHome();
    });

    options.chrome.append(this.#home.element, this.#gameChrome.element);

    const { fullscreen, events } = this.#engine.context;
    if (fullscreen.isSupported) {
      this.#fullscreenToggle = new FullscreenToggle(() => {
        this.#toggleFullscreen();
      });
      this.#fullscreenToggle.setActive(fullscreen.isActive);
      options.chrome.append(this.#fullscreenToggle.element);

      this.#unwatchFullscreen = events.on('fullscreenChanged', ({ isActive }) => {
        this.#fullscreenDeclined = !isActive;
        this.#fullscreenToggle?.setActive(isActive);
      });
    }
  }

  /** Starts the frame loop and shows whatever the current address names. */
  start(): void {
    this.#router.start();
    this.#engine.start();
  }

  /** Stops navigating and removes the chrome. Disposing the engine is the caller's business. */
  dispose(): void {
    this.#unwatchFullscreen?.();
    this.#router.dispose();
    this.#gameChrome.dispose();
    this.#fullscreenToggle?.dispose();
    this.#home.dispose();
  }

  /*
   * The fullscreen request rides the tap that chose the game, which is the only moment a browser will
   * grant one. #play runs for a bookmarked address and for the back gesture too, where there is no
   * interaction to spend and the request would simply be refused.
   */
  #choose(gameId: string): void {
    if (!this.#fullscreenDeclined) {
      void this.#engine.context.fullscreen.request();
    }
    this.#router.goToGame(gameId);
  }

  #toggleFullscreen(): void {
    const { fullscreen } = this.#engine.context;
    if (fullscreen.isActive) {
      fullscreen.exit();
      return;
    }
    void fullscreen.request();
  }

  /*
   * An unknown id replaces the address rather than pushing a new one, so the back gesture cannot walk
   * straight back into the same dead link. That covers a stale bookmark and a game removed from the
   * registry, which are the same case as far as the shell is concerned.
   */
  #navigate(gameId: string | undefined): void {
    if (gameId === undefined) {
      this.#showHome();
      return;
    }

    const game = findGame(gameId);
    if (game === undefined) {
      this.#router.replaceWithHome();
      return;
    }
    this.#play(game);
  }

  #showHome(): void {
    this.#engine.context.scenes.deactivate();
    this.#gameChrome.visible = false;
    this.#home.visible = true;
    this.#setToggleVisible(true);
  }

  #play(game: GameDefinition): void {
    this.#home.visible = false;
    this.#setToggleVisible(false);
    this.#gameChrome.setTitle(game.title);
    this.#gameChrome.visible = true;
    this.#engine.context.scenes.activate(game.createScene(this.#engine.context));
  }

  #setToggleVisible(visible: boolean): void {
    if (this.#fullscreenToggle !== undefined) {
      this.#fullscreenToggle.visible = visible;
    }
  }
}
